import { useEffect, useState, useCallback } from 'react'
import { collection, limit, query, where, orderBy } from 'firebase/firestore'
import { db, loggedGetDocs } from '../firebase'
import { subscribeToCollection } from '../utils/firestoreHelpers'
import { Expense } from '../types/expenseTypes'
import { useAuthContext } from '../context/AuthContext'

export const useExpenses = () => {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const { roles, assignedUnits, user } = useAuthContext()
  const isAdmin = roles.includes('admin')

  const refresh = useCallback(async () => {
    if (!user) return
    
    let expenseQuery
    
    if (isAdmin) {
      // Admin can see all expenses
      expenseQuery = query(
        collection(db, 'expenses'),
        orderBy('createdAt', 'desc'),
        limit(500) // Increased from 100 to 500 for more data
      )
    } else {
      // Non-admin users see only their assigned units
      if (assignedUnits.length === 0) {
        setExpenses([])
        return
      }
      
      expenseQuery = query(
        collection(db, 'expenses'),
        where('unitId', 'in', assignedUnits),
        orderBy('createdAt', 'desc'),
        limit(500) // Increased from 100 to 500 for more data
      )
    }
    
    const snap = await loggedGetDocs(expenseQuery)
    setExpenses(
      snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        date: d.data().date?.toDate?.() || d.data().date || null,
      })) as Expense[]
    )
  }, [user, assignedUnits, isAdmin])

  useEffect(() => {
    if (user && (isAdmin || assignedUnits.length > 0)) {
      refresh()
    }
  }, [user, assignedUnits, isAdmin, refresh])

  return { expenses, refresh }
}
